import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { isoOf, useAcList, useHikDevices } from "../api/accessControlApi";
import {
  CAMERA_ICON,
  ConsolePage,
  DetailRow,
  Drawer,
  FilterSelect,
  KpiRow,
  KpiTile,
  LoadState,
  Panel,
  SearchBox,
  SegmentedToggle,
  StatusChip,
  ToolButton,
  relativeTime,
  useTicker,
  type ChipTone,
} from "../components/console/Console";
import { NUVEX_ACCESS_BASE } from "../paths";

// ===========================================================================
// Dispositivos: inventario unificado de controladoras Hikvision (Patio de
// maniobras), cámaras y terminales de los puntos de acceso, con su estado.
// `mode`: tablero (KPIs + tipos + fuera de línea), inventario o controladoras.
// ===========================================================================

type Kind = "controller" | "camera" | "terminal";

interface DeviceRow {
  id: string;
  kind: Kind;
  name: string;
  model: string;
  serial: string;
  site: string;
  status: "online" | "offline" | "maintenance" | "alarm";
  lastSeen: string | null;
  photo: string;
  detail: string;
  ip?: string | null;
}

const PHOTO = (f: string) => `/sandbox/acceso/thumbs/${f}.webp`;
const STATUS_TONE: Record<DeviceRow["status"], ChipTone> = { online: "success", offline: "neutral", maintenance: "warning", alarm: "error" };

export default function DevicesConsole({ mode = "inventory" }: { mode?: "dashboard" | "inventory" | "controllers" }) {
  const { t, i18n } = useTranslation("accessConsole");
  const navigate = useNavigate();
  const now = useTicker();
  const hikQ = useHikDevices();
  const camerasQ = useAcList("cameras");
  const pointsQ = useAcList("points");

  const [kind, setKind] = useState<Kind | "">(mode === "controllers" ? "controller" : "");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [view, setView] = useState<"grid" | "table">(mode === "dashboard" ? "grid" : "table");
  const [openId, setOpenId] = useState<string | null>(null);

  const devices: DeviceRow[] = useMemo(() => {
    const out: DeviceRow[] = [];
    for (const d of hikQ.data ?? []) {
      out.push({
        id: `hik:${d.id}`,
        kind: "controller",
        name: d.device_name,
        model: d.controller_model ?? "Hikvision",
        serial: d.device_serial,
        site: d.yard_name ?? "—",
        status: d.status === "online" ? "online" : "offline",
        lastSeen: isoOf(d.last_event_at),
        photo: PHOTO("dev-lector-tarjeta"),
        detail: t("devices.controllerDetail", { interpolation: { escapeValue: false }, door: d.door_no, readers: d.readers?.length ?? 0, mode: d.connection_mode ?? "—" }),
      });
    }
    for (const c of camerasQ.data ?? []) {
      out.push({
        id: `cam:${c.id}`,
        kind: "camera",
        name: c.name,
        model: `${c.brand ?? ""} ${c.model ?? ""}`.trim(),
        serial: c.serial ?? "—",
        site: c.site_name ?? "—",
        status: c.status === "recording" ? "online" : (c.status as DeviceRow["status"]),
        lastSeen: c.last_seen_at ?? null,
        photo: PHOTO(c.type === "bullet" || c.type === "ptz" ? "dev-camara-bala" : "dev-camara-domo"),
        detail: `${t(`cameraType.${c.type ?? "dome"}`)} · ${c.resolution ?? ""} · CH${String(c.channel ?? 1).padStart(2, "0")}`,
        ip: c.ip_masked,
      });
    }
    for (const p of pointsQ.data ?? []) {
      out.push({
        id: `pt:${p.id}`,
        kind: "terminal",
        name: p.name,
        model: p.device_model ?? "—",
        serial: p.device_serial ?? "—",
        site: p.site_name ?? "—",
        status: p.status,
        lastSeen: p.last_event_at ?? null,
        photo: PHOTO(p.type === "turnstile" ? "dev-torniquete" : p.type === "barrier" || p.type === "gate" ? "dev-pluma" : "dev-lector-tarjeta"),
        detail: t(`pointType.${p.type}`),
      });
    }
    return out;
  }, [hikQ.data, camerasQ.data, pointsQ.data, t]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return devices.filter((d) => (!kind || d.kind === kind) && (!status || d.status === status) && (!q || `${d.name} ${d.model} ${d.serial} ${d.site}`.toLowerCase().includes(q)));
  }, [devices, kind, status, search]);
  const current = devices.find((d) => d.id === openId) ?? null;
  const loading = hikQ.isLoading || camerasQ.isLoading || pointsQ.isLoading;
  const count = (k: Kind, st?: DeviceRow["status"]) => devices.filter((d) => d.kind === k && (!st || d.status === st)).length;
  const down = devices.filter((d) => d.status !== "online");

  const title = mode === "dashboard" ? t("devices.dashboardTitle") : mode === "controllers" ? t("devices.controllersTitle") : t("devices.title");

  return (
    <ConsolePage icon="ri-cpu-line" title={title} subtitle={t("devices.subtitle")} actions={<ToolButton icon="ri-truck-line" onClick={() => navigate("/truck-yard")}>{t("devices.manageInYard")}</ToolButton>}>
      <KpiRow>
        <KpiTile icon="ri-cpu-line" label={t("devices.total")} value={devices.length} />
        <KpiTile icon="ri-checkbox-circle-line" tone="success" label={t("devices.online")} value={devices.filter((d) => d.status === "online").length} />
        <KpiTile icon="ri-wifi-off-line" tone="neutral" label={t("devices.offline")} value={devices.filter((d) => d.status === "offline").length} />
        <KpiTile icon="ri-router-line" tone="info" label={t("devices.kind.controller")} value={`${count("controller", "online")} / ${count("controller")}`} />
        <KpiTile icon={CAMERA_ICON} tone="primary" label={t("devices.kind.camera")} value={`${count("camera", "online")} / ${count("camera")}`} />
        <KpiTile icon="ri-fingerprint-line" tone="warning" label={t("devices.kind.terminal")} value={`${count("terminal", "online")} / ${count("terminal")}`} />
      </KpiRow>

      {mode === "dashboard" && (
        <div className="grid gap-4 lg:grid-cols-3">
          {(["controller", "camera", "terminal"] as Kind[]).map((k) => (
            <button key={k} type="button" onClick={() => navigate(`${NUVEX_ACCESS_BASE}/dispositivos/${k === "controller" ? "controladoras" : "inventario"}`)} className="flex items-center gap-4 overflow-hidden rounded-xl border border-line bg-card p-3 text-left hover:shadow-md">
              <img src={PHOTO(k === "controller" ? "dev-lector-tarjeta" : k === "camera" ? "dev-camara-domo" : "dev-torniquete")} alt="" className="h-20 w-28 shrink-0 rounded-lg object-cover" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-content">{t(`devices.kind.${k}`)}</p>
                <p className="text-2xl font-bold tabular-nums text-content">{count(k)}</p>
                <div className="mt-1 flex flex-wrap gap-1">
                  <StatusChip tone="success">{t("devices.onlineN", { interpolation: { escapeValue: false }, count: count(k, "online") })}</StatusChip>
                  {count(k) - count(k, "online") > 0 && <StatusChip tone="warning">{t("devices.attentionN", { interpolation: { escapeValue: false }, count: count(k) - count(k, "online") })}</StatusChip>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {mode === "dashboard" ? (
        <Panel title={t("devices.needsAttention")} icon="ri-error-warning-line" bodyClassName="max-h-[420px] overflow-y-auto">
          <LoadState loading={loading} error={hikQ.error || camerasQ.error} empty={!down.length} emptyText={t("devices.allOk")}>
            <ul className="divide-y divide-line">
              {down.map((d) => (
                <li key={d.id} className="flex items-center gap-3 px-3 py-2">
                  <img src={d.photo} alt="" className="h-10 w-14 rounded object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-content">{d.name}</p>
                    <p className="truncate text-xs text-content-secondary">{t(`devices.kind.${d.kind}`)} · {d.site} · {t("fields.lastSeen")}: {relativeTime(d.lastSeen, i18n.language, now)}</p>
                  </div>
                  <StatusChip tone={STATUS_TONE[d.status]}>{t(`pointStatus.${d.status}`)}</StatusChip>
                </li>
              ))}
            </ul>
          </LoadState>
        </Panel>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <SearchBox value={search} onChange={setSearch} placeholder={t("devices.searchPlaceholder")} />
            {mode !== "controllers" && (
              <FilterSelect label={t("fields.type")} value={kind} onChange={(v) => setKind(v as Kind | "")} options={[{ value: "", label: t("devices.allKinds") }, ...(["controller", "camera", "terminal"] as Kind[]).map((k) => ({ value: k, label: t(`devices.kind.${k}`) }))]} />
            )}
            <FilterSelect label={t("fields.status")} value={status} onChange={setStatus} options={[{ value: "", label: t("common.allStatuses") }, ...(["online", "offline", "maintenance", "alarm"] as const).map((s) => ({ value: s, label: t(`pointStatus.${s}`) }))]} />
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-content-muted">{t("common.resultsN", { interpolation: { escapeValue: false }, count: rows.length })}</span>
              <SegmentedToggle label={t("common.viewMode")} value={view} onChange={setView} options={[{ value: "grid", label: <i className="ri-layout-grid-line" />, title: t("common.grid") }, { value: "table", label: <i className="ri-list-check" />, title: t("common.table") }]} />
            </div>
          </div>
          <LoadState loading={loading} error={hikQ.error && camerasQ.error} empty={!rows.length} emptyText={t("devices.empty")}>
            {view === "grid" ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {rows.map((d) => (
                  <button key={d.id} type="button" onClick={() => setOpenId(d.id)} className="overflow-hidden rounded-xl border border-line bg-card text-left hover:shadow-md">
                    <img src={d.photo} alt="" loading="lazy" className="aspect-[4/3] w-full object-cover" />
                    <div className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <p className="min-w-0 truncate text-sm font-semibold text-content">{d.name}</p>
                        <StatusChip tone={STATUS_TONE[d.status]}>{t(`pointStatus.${d.status}`)}</StatusChip>
                      </div>
                      <p className="truncate text-xs text-content-secondary">{d.model} · {d.site}</p>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <Panel bodyClassName="overflow-x-auto">
                <table className="w-full min-w-[820px] text-sm">
                  <thead className="bg-subtle text-left text-[11px] uppercase tracking-wide text-content-muted">
                    <tr>
                      <th className="px-3 py-2">{t("fields.name")}</th>
                      <th className="px-3 py-2">{t("fields.type")}</th>
                      <th className="px-3 py-2">{t("fields.model")}</th>
                      <th className="px-3 py-2">{t("fields.serial")}</th>
                      <th className="px-3 py-2">{t("fields.site")}</th>
                      <th className="px-3 py-2">{t("fields.lastSeen")}</th>
                      <th className="px-3 py-2">{t("fields.status")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {rows.map((d) => (
                      <tr key={d.id} onClick={() => setOpenId(d.id)} className="cursor-pointer hover:bg-subtle">
                        <td className="px-3 py-2"><div className="flex items-center gap-2"><img src={d.photo} alt="" className="h-8 w-10 rounded object-cover" /><span className="font-medium text-content">{d.name}</span></div></td>
                        <td className="px-3 py-2 text-content-secondary">{t(`devices.kind.${d.kind}`)}</td>
                        <td className="px-3 py-2 text-content-secondary">{d.model}</td>
                        <td className="px-3 py-2 font-mono text-xs text-content-secondary">{d.serial}</td>
                        <td className="px-3 py-2 text-content-secondary">{d.site}</td>
                        <td className="px-3 py-2 text-content-secondary">{relativeTime(d.lastSeen, i18n.language, now)}</td>
                        <td className="px-3 py-2"><StatusChip tone={STATUS_TONE[d.status]}>{t(`pointStatus.${d.status}`)}</StatusChip></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Panel>
            )}
          </LoadState>
        </>
      )}

      <Drawer open={Boolean(current)} title={current?.name ?? ""} subtitle={current ? t(`devices.kind.${current.kind}`) : undefined} onClose={() => setOpenId(null)}>
        {current && (
          <div className="space-y-3">
            <img src={current.photo.replace("/thumbs/", "/")} alt="" className="aspect-[4/3] w-full rounded-lg object-cover" />
            <StatusChip tone={STATUS_TONE[current.status]}>{t(`pointStatus.${current.status}`)}</StatusChip>
            <div>
              <DetailRow label={t("fields.model")}>{current.model}</DetailRow>
              <DetailRow label={t("fields.serial")}><span className="font-mono text-xs">{current.serial}</span></DetailRow>
              <DetailRow label={t("fields.site")}>{current.site}</DetailRow>
              <DetailRow label={t("fields.details")}>{current.detail}</DetailRow>
              {current.ip && <DetailRow label={t("fields.ip")}><span className="font-mono">{current.ip}</span></DetailRow>}
              <DetailRow label={t("fields.lastSeen")}>{relativeTime(current.lastSeen, i18n.language, now)}</DetailRow>
            </div>
          </div>
        )}
      </Drawer>
    </ConsolePage>
  );
}
